import {Anchor} from 'src/design/atoms/Anchor';
import {Box} from 'src/design/atoms/Box';
import {Group} from 'src/design/atoms/Group';
import {IconFile} from '@tabler/icons-react';
import React from 'react';
import {HelpText} from 'src/design/atoms/HelpText';
import {useCachedBlobUrls} from 'src/offline/useAttachmentUrl';
import {useConnectivity} from 'src/offline/useConnectivity';
import {useAppSelector} from 'src/redux/hooks';
import {getUrlWithEncodingAndAuth} from 'src/shared/utils/urlHelpers';
import type {IFormDocumentUploadFile} from '../FormFileDropzone/FormFileUploadV2';
import {useFileUtilities} from '../FormFileDropzone/hooks/useFileUtilities';
import {ImagePreview} from './ImagePreview';
import {PdfPages} from './PdfPages';

export interface FileListViewProps {
  files: IFormDocumentUploadFile[];
  renderAsImage?: boolean;
}

/** Storage key a cached blob is filed under; matches the offline bundle. */
const fileRefKey = (file: IFormDocumentUploadFile): string =>
  file.encodedKey || file.value;

const isPdfFile = (file: IFormDocumentUploadFile): boolean =>
  file.type === 'application/pdf' ||
  !!file.name?.toLowerCase().endsWith('.pdf');

export const FileListView: React.FC<FileListViewProps> = ({
  files,
  renderAsImage,
}) => {
  const {isImageByMimeType} = useFileUtilities();
  const {accessToken} = useAppSelector(state => state.auth);
  const {isOffline} = useConnectivity();
  const cachedUrls = useCachedBlobUrls(files.map(fileRefKey));

  // Offline the network url is unreachable, so only a cached blob can be shown.
  const resolveUrl = (file: IFormDocumentUploadFile): string | undefined => {
    const cached = cachedUrls[fileRefKey(file)];
    if (cached) return cached;
    return isOffline || !file.value
      ? undefined
      : getUrlWithEncodingAndAuth(file.value, accessToken);
  };

  const handleOpenFile = (url: string | undefined) => {
    if (!url) return;
    window.open(url, '_blank');
  };

  return (
    <Box pt="sm">
      {files.map(file => {
        const url = resolveUrl(file);
        if (renderAsImage && isPdfFile(file)) {
          return (
            <Box key={file.value ?? 'no-value'} pb="lg">
              {url ? (
                <PdfPages src={url} title={file.name} />
              ) : (
                <HelpText text="Attachment not cached on this device." />
              )}
            </Box>
          );
        }
        return (
          <Box key={file.value ?? 'no-value'} pb="lg">
            <Anchor
              onClick={() => handleOpenFile(url)}
              pb="sm"
              c="black"
              underline="hover">
              <Group>
                <Box>
                  <IconFile />
                </Box>
                <Box ml="md">{file.name}</Box>
              </Group>
            </Anchor>
            {!url && <HelpText text="Attachment not cached on this device." />}
            {url && (renderAsImage || isImageByMimeType(file.type)) && (
              <ImagePreview src={url} alt="File preview" />
            )}
          </Box>
        );
      })}
    </Box>
  );
};
